import type { AppLocale } from '../i18n';
import type { BlogPost, ResolvedBlogPost } from '../types/blog';
import { ALL_POSTS } from '../content/blog';
import { I18N_ROUTING_ENABLED } from '../i18n/locales';
import { localizedPath } from '../i18n/slugMap';

const SITE_URL = 'https://bolaoelite.com.br';

/**
 * Resolve um `BlogPost` (registry por locale) para um idioma específico.
 * Se o idioma não tiver variante, cai no pt-BR (fallback absoluto).
 */
function resolvePost(post: BlogPost, locale: AppLocale): ResolvedBlogPost {
  const variant = post.i18n[locale] ?? post.i18n['pt-BR'];
  return {
    ...variant,
    category: post.category,
    author: post.author,
    publishedAt: post.publishedAt,
    // coverImage por idioma quando disponível (`{slug}-en.svg` etc.);
    // fallback pra capa PT do nível BlogPost preserva retrocompat com
    // posts antigos que ainda não foram traduzidos.
    coverImage: variant.coverImage ?? post.coverImage,
    tags: post.tags,
    locale,
  };
}

/** Todos os posts no idioma informado, em ordem cronológica reversa. */
export function getAllPosts(locale: AppLocale): ResolvedBlogPost[] {
  return ALL_POSTS
    .map((p) => resolvePost(p, locale))
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
}

/**
 * Busca post pelo slug. Tenta primeiro o slug do idioma ativo; se não casar
 * (ex.: slug pt-BR acessado sob prefixo /en por um link cruzado antigo),
 * tenta os demais idiomas — evita 404 — mas devolve sempre no idioma `locale`.
 * Retorna null se nenhum post tiver esse slug em idioma algum.
 */
export function getPostBySlug(slug: string, locale: AppLocale): ResolvedBlogPost | null {
  const exact = ALL_POSTS.find((p) => p.i18n[locale]?.slug === slug);
  if (exact) return resolvePost(exact, locale);

  const anyLocale = ALL_POSTS.find((p) =>
    (Object.keys(p.i18n) as AppLocale[]).some((l) => p.i18n[l]?.slug === slug)
  );
  return anyLocale ? resolvePost(anyLocale, locale) : null;
}

/**
 * hreflang alternates (pt-BR/en/es + x-default) de um post — pro componente SEO.
 * Usa os slugs traduzidos (post.i18n[l].slug) sob o /blog localizado de cada
 * idioma. Retorna [] com o roteamento i18n OFF (sem hreflang, = comportamento
 * anterior) ou se o slug não casar com post algum.
 */
export function getPostAlternates(slug: string): Array<{ hreflang: string; href: string }> {
  if (!I18N_ROUTING_ENABLED) return [];
  const post = ALL_POSTS.find((p) =>
    (Object.keys(p.i18n) as AppLocale[]).some((l) => p.i18n[l]?.slug === slug)
  );
  if (!post) return [];

  const entries = (['pt-BR', 'en', 'es'] as AppLocale[])
    .filter((l) => post.i18n[l]?.slug)
    .map((l) => ({
      hreflang: l as string,
      href: `${SITE_URL}${localizedPath('blog', l)}/${post.i18n[l]!.slug}`,
    }));

  const ptSlug = post.i18n['pt-BR']?.slug;
  if (ptSlug) {
    entries.push({ hreflang: 'x-default', href: `${SITE_URL}/blog/${ptSlug}` });
  }
  return entries;
}

/**
 * Retorna até `limit` posts relacionados ao slug informado, no idioma ativo.
 * Heurística: prioriza mesma categoria, depois posts com tags em comum.
 */
export function getRelatedPosts(
  slug: string,
  locale: AppLocale,
  limit = 3
): ResolvedBlogPost[] {
  const current = getPostBySlug(slug, locale);
  if (!current) return [];

  const candidates = getAllPosts(locale).filter((p) => p.slug !== current.slug);

  const scored = candidates.map((p) => {
    const sameCategory = p.category === current.category ? 5 : 0;
    const sharedTags = p.tags.filter((t) => current.tags.includes(t)).length;
    return { post: p, score: sameCategory + sharedTags };
  });

  return scored
    .sort((a, b) => b.score - a.score || b.post.publishedAt.localeCompare(a.post.publishedAt))
    .slice(0, limit)
    .map((s) => s.post);
}

/** Pagina os posts no idioma informado. Retorna a fatia + metadados de UI. */
export function paginatePosts(
  page: number,
  locale: AppLocale,
  perPage = 12
): {
  posts: ResolvedBlogPost[];
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  hasPrev: boolean;
  hasNext: boolean;
} {
  const all = getAllPosts(locale);
  const total = all.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.max(1, Math.min(page, totalPages));
  const start = (safePage - 1) * perPage;

  return {
    posts: all.slice(start, start + perPage),
    page: safePage,
    perPage,
    total,
    totalPages,
    hasPrev: safePage > 1,
    hasNext: safePage < totalPages,
  };
}

/**
 * Formata uma data ISO yyyy-mm-dd no formato longo do idioma informado.
 * Usa `Intl.DateTimeFormat` em UTC (a data é só dia, sem hora — evita o
 * deslize de fuso). pt-BR/es: "15 de abril de 2026"; en: "April 15, 2026".
 */
export function formatPostDate(isoDate: string, locale: AppLocale): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}
